import axios from 'axios';

/**
 * Fetch owned games from Steam Web API
 * @param {string} steamid - Optional Steam ID. If not provided, uses server default.
 * @param {object} options - Optional parameters
 * @param {boolean} options.includeAppInfo - Include game names and info (default: true)
 * @param {boolean} options.includePlayedFreeGames - Include free games (default: true)
 * @returns {Promise} Axios response with game data
 */
export async function getOwnedGames(steamid = null, options = {}) {
    const params = {};

    // Add steamid if provided
    if (steamid) {
        params.steamid = steamid;
    }

    // Add optional parameters
    if (options.includeAppInfo !== undefined) {
        params.include_appinfo = options.includeAppInfo ? '1' : '0';
    }

    if (options.includePlayedFreeGames !== undefined) {
        params.include_played_free_games = options.includePlayedFreeGames ? '1' : '0';
    }

    return await axios.get("/api/steam/owned-games", { params });
}

/**
 * Fetch achievement statistics for multiple games at once
 * @param {Array<number>} appids - Array of app IDs to fetch achievement stats for
 * @param {string} steamid - Optional Steam ID. If not provided, uses server default.
 * @returns {Promise} Axios response with achievement stats for each game
 */
export async function getBulkAchievementStats(appids, steamid = null) {
    const body = {
        appids: appids
    };

    if (steamid) {
        body.steamid = steamid;
    }

    return await axios.post("/api/steam/bulk-achievement-stats", body);
}
/**
 * When the Steam library was last refreshed and whether a refresh is running
 * @returns {Promise} Axios response with last_refreshed_at, last_run, is_running, next_run_at
 */
export async function getSteamRefreshStatus() {
    return await axios.get("/api/steam/refresh-status");
}

/**
 * Admin overview: refresh status, 30-day run stats, run history and library totals
 * @param {string} token - Admin JWT
 * @param {number} limit - Number of history rows
 */
export async function getSteamAdminOverview(token, limit = 20) {
    return await axios.get("/api/steam/admin/overview", {
        params: { limit },
        headers: { 'Authorization': `Bearer ${token}` }
    });
}

/**
 * Queue a manual Steam library refresh (runs in the background on the server)
 * @param {string} token - Admin JWT
 */
export async function triggerSteamRefresh(token) {
    return await axios.post("/api/steam/admin/refresh", {}, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
}

/**
 * Steam insights registries: completion metrics, recommendation signals and valid grains
 */
export async function getSteamInsightMetrics() {
    return await axios.get("/api/steam/insights/metrics");
}

/**
 * Completion % over time
 * @param {string} metric - Metric key from getSteamInsightMetrics
 * @param {string} grain - day | week | month | year
 */
export async function getSteamCompletionSeries(metric, grain) {
    return await axios.get("/api/steam/insights/completion", { params: { metric, grain } });
}

/**
 * Unlocks, games started and games completed per period
 * @param {string} grain - day | week | month | year
 */
export async function getSteamActivitySeries(grain) {
    return await axios.get("/api/steam/insights/activity", { params: { grain } });
}

/**
 * Games ranked by recommendation score
 * @param {number} limit - Number of games (1-50)
 */
export async function getSteamNextPlay(limit = 10) {
    return await axios.get("/api/steam/insights/next-play", { params: { limit } });
}

/**
 * Unplayed library games with backlog scores
 */
export async function getSteamUnplayed() {
    return await axios.get("/api/steam/insights/unplayed");
}

/**
 * Per-game community unlock rates alongside your completion
 */
export async function getSteamCommunity() {
    return await axios.get("/api/steam/insights/community");
}
